import React from 'react';
import {
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Pagination,
  Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import RestaurantIcon from '@mui/icons-material/Restaurant';

interface GtsProduct {
  id: string;
  name: string;
  stockCode: string;
  salePrice: number;
  isQrMenu: boolean;
  isKiosk: boolean;
  categoryName?: string;
  imageUrl?: string;
}

interface HaciogluProduct {
  id: number;
  stokKodu: number;
  stokAdi: string;
  urunGurubu?: string;
  aktif: boolean;
  fiyat?: number;
  imageUrl?: string;
}

interface Props {
  loading: boolean;
  gtsProducts: GtsProduct[];
  haciogluProducts: HaciogluProduct[];
  mappedProductIds: Set<string>;
  selectedGtsProduct: GtsProduct | null;
  selectedHaciogluProduct: HaciogluProduct | null;
  onSelectGtsProduct: (product: GtsProduct) => void;
  onSelectHaciogluProduct: (product: HaciogluProduct) => void;
  onCreateMapping: () => void;
}

const HaciogluMapping: React.FC<Props> = ({
  loading,
  gtsProducts,
  haciogluProducts,
  mappedProductIds,
  selectedGtsProduct,
  selectedHaciogluProduct,
  onSelectGtsProduct,
  onSelectHaciogluProduct,
  onCreateMapping
}) => {
  const [gtsSearch, setGtsSearch] = React.useState('');
  const [haciogluSearch, setHaciogluSearch] = React.useState('');
  const [gtsFilter, setGtsFilter] = React.useState<'all' | 'mapped' | 'unmapped'>('all');
  const [gtsPage, setGtsPage] = React.useState(1);
  const [haciogluPage, setHaciogluPage] = React.useState(1);
  const itemsPerPage = 10;

  // Filtreleme
  const filteredGtsProducts = gtsProducts.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(gtsSearch.toLowerCase()) ||
      p.stockCode.toLowerCase().includes(gtsSearch.toLowerCase());
    const matchesFilter = gtsFilter === 'all' ||
      (gtsFilter === 'mapped' && mappedProductIds.has(p.id)) ||
      (gtsFilter === 'unmapped' && !mappedProductIds.has(p.id));
    return matchesSearch && matchesFilter;
  });

  const filteredHaciogluProducts = haciogluProducts.filter(p =>
    p.stokAdi.toLowerCase().includes(haciogluSearch.toLowerCase()) ||
    p.stokKodu.toString().includes(haciogluSearch)
  );

  const paginatedGtsProducts = filteredGtsProducts.slice((gtsPage - 1) * itemsPerPage, gtsPage * itemsPerPage);
  const paginatedHaciogluProducts = filteredHaciogluProducts.slice((haciogluPage - 1) * itemsPerPage, haciogluPage * itemsPerPage);

  return (
    <Grid container spacing={3}>
      {/* Sol Panel - GTS Ürünleri */}
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            GTS Products
          </Typography>
          
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search product..."
              value={gtsSearch}
              onChange={(e) => setGtsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box display="flex" gap={1} mb={2}>
            <Button
              size="small"
              variant={gtsFilter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setGtsFilter('all')}
            >
              All
            </Button>
            <Button
              size="small"
              variant={gtsFilter === 'unmapped' ? 'contained' : 'outlined'}
              onClick={() => setGtsFilter('unmapped')}
            >
              Unmatched
            </Button>
            <Button
              size="small"
              variant={gtsFilter === 'mapped' ? 'contained' : 'outlined'}
              onClick={() => setGtsFilter('mapped')}
            >
              Matched
            </Button>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : paginatedGtsProducts.length === 0 ? (
              <Alert severity="info">No products found</Alert>
            ) : (
              paginatedGtsProducts.map((product) => (
                <Paper
                  key={product.id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    cursor: 'pointer',
                    border: selectedGtsProduct?.id === product.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                  onClick={() => onSelectGtsProduct(product)}
                >
                  <Avatar
                    src={product.imageUrl}
                    alt={product.name}
                    sx={{ width: 50, height: 50, bgcolor: '#e3f2fd' }}
                  >
                    <FastfoodIcon sx={{ color: '#1976d2' }} />
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{product.name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Code: {product.stockCode}
                    </Typography>
                    <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                      {product.isQrMenu && <Chip label="QR" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                      {product.isKiosk && <Chip label="Kiosk" size="small" color="secondary" sx={{ height: 20, fontSize: '0.65rem' }} />}
                      {mappedProductIds.has(product.id) && (
                        <Chip label="✓" size="small" color="success" sx={{ height: 20, minWidth: 24 }} />
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>

          <Pagination
            count={Math.ceil(filteredGtsProducts.length / itemsPerPage)}
            page={gtsPage}
            onChange={(e, page) => setGtsPage(page)}
            size="small"
          />
        </Paper>
      </Grid>

      {/* Ortada Eşleştirme Butonu */}
      <Grid item xs={12} md={2} display="flex" alignItems="center" justifyContent="center">
        <Box textAlign="center">
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onCreateMapping}
            disabled={!selectedGtsProduct || !selectedHaciogluProduct || loading}
            startIcon={<CompareArrowsIcon />}
            sx={{ mb: 2 }}
          >
            Match
          </Button>
          {selectedGtsProduct && (
            <Paper sx={{ p: 1, mb: 1, bgcolor: '#e3f2fd' }}>
              <Typography variant="caption" display="block">Selected GTS:</Typography>
              <Typography variant="body2" fontWeight="bold">{selectedGtsProduct.name}</Typography>
            </Paper>
          )}
          {selectedHaciogluProduct && (
            <Paper sx={{ p: 1, bgcolor: '#e8f5e9' }}>
              <Typography variant="caption" display="block">Selected Hacioglu:</Typography>
              <Typography variant="body2" fontWeight="bold">{selectedHaciogluProduct.stokAdi}</Typography>
            </Paper>
          )}
        </Box>
      </Grid>

      {/* Sağ Panel - Hacıoğlu Ürünleri */}
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            Hacioglu Products
          </Typography>
          
          <TextField
            fullWidth
            size="small"
            placeholder="Search product..."
            value={haciogluSearch}
            onChange={(e) => setHaciogluSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : paginatedHaciogluProducts.length === 0 ? (
              <Alert severity="info">No products found</Alert>
            ) : (
              paginatedHaciogluProducts.map((product) => (
                <Paper
                  key={product.id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    cursor: 'pointer',
                    border: selectedHaciogluProduct?.stokKodu === product.stokKodu ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                  onClick={() => onSelectHaciogluProduct(product)}
                >
                  <Avatar sx={{ width: 50, height: 50, bgcolor: '#e8f5e9' }}>
                    <RestaurantIcon sx={{ color: '#4caf50' }} />
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{product.stokAdi}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Code: {product.stokKodu}
                    </Typography>
                    {product.fiyat && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Price: {product.fiyat} TL
                      </Typography>
                    )}
                    {product.urunGurubu && (
                      <Box mt={0.5}>
                        <Chip label={product.urunGurubu} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))
            )}
          </Box>

          <Pagination
            count={Math.ceil(filteredHaciogluProducts.length / itemsPerPage)}
            page={haciogluPage}
            onChange={(e, page) => setHaciogluPage(page)}
            size="small"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default HaciogluMapping;

